import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../../lib/utils';

/** Wrap the app once so `Tooltip` / `Tip` work anywhere below it. */
export function TooltipProvider({
  delayDuration = 200,
  skipDelayDuration = 300,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export function TooltipContent({
  className,
  sideOffset = 7,
  collisionPadding = 10,
  children,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        // Visual style + entrance/exit live in `.tooltip-panel` (styles.css)
        // so the animation can key off Radix's `data-side` / `data-state`
        // and the `--radix-tooltip-content-transform-origin` var.
        className={cn('tooltip-panel z-[60] text-pretty', className)}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

interface TipProps {
  /** Tooltip body — plain text or rich nodes. */
  label: ReactNode;
  children: ReactNode;
  /** Preferred side; Radix flips it if there's no room. */
  side?: ComponentProps<typeof TooltipPrimitive.Content>['side'];
  /** Pass when the child is a single focusable/hoverable element (a button,
   * an icon). Defaults to true — the common case. */
  asChild?: boolean;
}

/**
 * The one-liner form: `<Tip label="…"><button/></Tip>`. For anything more
 * involved, compose `Tooltip` / `TooltipTrigger` / `TooltipContent` directly.
 */
export function Tip({ label, children, side, asChild = true }: TipProps) {
  if (label == null || label === '') return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild={asChild}>{children}</TooltipTrigger>
      <TooltipContent {...(side ? { side } : {})}>{label}</TooltipContent>
    </Tooltip>
  );
}
